import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box, Typography, Paper, Button, Divider, TextField,
  InputAdornment, IconButton, CircularProgress, Switch,
  Card, CardContent, CardActionArea, Skeleton,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material'
import { Edit, Check, Close, Visibility, VisibilityOff, FamilyRestroom, Notifications } from '@mui/icons-material'
import { useAuth } from '@/contexts/AuthContext'
import { useSnackbar } from 'notistack'
import { usePushNotifications } from '@/hooks/usePushNotifications'
import { apiClient } from '@/services/apiClient'
import { AccountProfile, FamilySummary } from '@/types'

export default function Profile() {
  const { user, logout, refreshUser } = useAuth()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { enabled: notificationsEnabled, loading: notificationsLoading, permissionState, toggle: toggleNotifications } = usePushNotifications()

  // Display name editing
  const [editingName, setEditingName] = useState(false)
  const [displayName, setDisplayName] = useState('')
  const [savingName, setSavingName] = useState(false)

  // Email editing
  const [editingEmail, setEditingEmail] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [savingEmail, setSavingEmail] = useState(false)

  // Password editing
  const [editingPassword, setEditingPassword] = useState(false)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)

  // Family
  const [family, setFamily] = useState<FamilySummary | null>(null)
  const [loadingFamily, setLoadingFamily] = useState(true)
  const [createFamilyOpen, setCreateFamilyOpen] = useState(false)
  const [newFamilyName, setNewFamilyName] = useState('')
  const [creatingFamily, setCreatingFamily] = useState(false)

  // About Me
  const [aboutMe, setAboutMe] = useState('')
  const [editingAboutMe, setEditingAboutMe] = useState(false)
  const [savingAboutMe, setSavingAboutMe] = useState(false)

  useEffect(() => {
    const loadFamily = async () => {
      try {
        const data = await apiClient.get<FamilySummary | null>('/api/families')
        setFamily(data)
      } catch {
        enqueueSnackbar('Failed to load family', { variant: 'error' })
      } finally {
        setLoadingFamily(false)
      }
    }
    const loadProfile = async () => {
      try {
        const profile = await apiClient.get<AccountProfile>('/api/account/profile')
        setAboutMe(profile.aboutMe ?? '')
      } catch {
        // profile load is non-critical
      }
    }
    loadFamily()
    loadProfile()
  }, [enqueueSnackbar])

  const handleLogout = async () => {
    await logout()
    enqueueSnackbar('Logged out', { variant: 'success' })
    navigate('/login')
  }

  const startEditName = () => {
    setDisplayName(user?.displayName ?? '')
    setEditingName(true)
  }

  const cancelEditName = () => {
    setEditingName(false)
    setDisplayName('')
  }

  const saveName = async () => {
    if (!displayName.trim()) return
    setSavingName(true)
    try {
      await apiClient.put<AccountProfile>('/api/account/display-name', { displayName: displayName.trim() })
      await refreshUser()
      enqueueSnackbar('Display name updated', { variant: 'success' })
      setEditingName(false)
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update display name', { variant: 'error' })
    } finally {
      setSavingName(false)
    }
  }

  const startEditEmail = () => {
    setNewEmail(user?.email ?? '')
    setEditingEmail(true)
  }

  const cancelEditEmail = () => {
    setEditingEmail(false)
    setNewEmail('')
  }

  const saveEmail = async () => {
    if (!newEmail.trim()) return
    setSavingEmail(true)
    try {
      await apiClient.put<AccountProfile>('/api/account/email', { newEmail: newEmail.trim() })
      await refreshUser()
      enqueueSnackbar('Email updated', { variant: 'success' })
      setEditingEmail(false)
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update email', { variant: 'error' })
    } finally {
      setSavingEmail(false)
    }
  }

  const cancelEditPassword = () => {
    setEditingPassword(false)
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setShowCurrentPassword(false)
    setShowNewPassword(false)
  }

  const savePassword = async () => {
    if (newPassword !== confirmPassword) {
      enqueueSnackbar('Passwords do not match', { variant: 'error' })
      return
    }
    if (!currentPassword || !newPassword) return
    setSavingPassword(true)
    try {
      await apiClient.put('/api/account/password', { currentPassword, newPassword })
      enqueueSnackbar('Password changed', { variant: 'success' })
      cancelEditPassword()
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to change password', { variant: 'error' })
    } finally {
      setSavingPassword(false)
    }
  }

  const startEditAboutMe = () => {
    setEditingAboutMe(true)
  }

  const cancelEditAboutMe = () => {
    setEditingAboutMe(false)
  }

  const saveAboutMe = async () => {
    setSavingAboutMe(true)
    try {
      const profile = await apiClient.put<AccountProfile>('/api/account/about-me', { aboutMe: aboutMe.trim() })
      setAboutMe(profile.aboutMe ?? '')
      enqueueSnackbar('About Me updated', { variant: 'success' })
      setEditingAboutMe(false)
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to update About Me', { variant: 'error' })
    } finally {
      setSavingAboutMe(false)
    }
  }

  const handleCreateFamily = async () => {
    if (!newFamilyName.trim()) return
    setCreatingFamily(true)
    try {
      const created = await apiClient.post<FamilySummary>('/api/families', { name: newFamilyName.trim() })
      setFamily(created)
      setCreateFamilyOpen(false)
      setNewFamilyName('')
      enqueueSnackbar('Family created', { variant: 'success' })
      navigate(`/families/${created.id}`)
    } catch (error) {
      enqueueSnackbar(error instanceof Error ? error.message : 'Failed to create family', { variant: 'error' })
    } finally {
      setCreatingFamily(false)
    }
  }

  if (!user?.isAuthenticated) {
    return null
  }

  return (
    <Box sx={{ maxWidth: 480, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
        Profile
      </Typography>

      {/* Display Name */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2" color="text.secondary">Display Name</Typography>
          {!editingName && (
            <IconButton size="small" onClick={startEditName} aria-label="edit display name">
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>
        {editingName ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              fullWidth
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoFocus
              disabled={savingName}
              onKeyDown={(e) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') cancelEditName() }}
            />
            <IconButton size="small" onClick={saveName} disabled={savingName || !displayName.trim()} color="primary">
              {savingName ? <CircularProgress size={20} /> : <Check />}
            </IconButton>
            <IconButton size="small" onClick={cancelEditName} disabled={savingName}>
              <Close />
            </IconButton>
          </Box>
        ) : (
          <Typography variant="body1" sx={{ fontWeight: 600 }}>{user.displayName}</Typography>
        )}
      </Paper>

      {/* Email */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2" color="text.secondary">Email</Typography>
          {!editingEmail && (
            <IconButton size="small" onClick={startEditEmail} aria-label="edit email">
              <Edit fontSize="small" />
            </IconButton>
          )}
        </Box>
        {editingEmail ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              fullWidth
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              autoFocus
              disabled={savingEmail}
              onKeyDown={(e) => { if (e.key === 'Enter') saveEmail(); if (e.key === 'Escape') cancelEditEmail() }}
            />
            <IconButton size="small" onClick={saveEmail} disabled={savingEmail || !newEmail.trim()} color="primary">
              {savingEmail ? <CircularProgress size={20} /> : <Check />}
            </IconButton>
            <IconButton size="small" onClick={cancelEditEmail} disabled={savingEmail}>
              <Close />
            </IconButton>
          </Box>
        ) : (
          <Typography variant="body1">{user.email}</Typography>
        )}
      </Paper>

      {/* Password */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2" color="text.secondary">Password</Typography>
          {!editingPassword && (
            <Button size="small" onClick={() => setEditingPassword(true)} startIcon={<Edit fontSize="small" />}>
              Change
            </Button>
          )}
        </Box>
        {editingPassword ? (
          <Box component="form" onSubmit={(e) => { e.preventDefault(); savePassword() }} sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              size="small"
              fullWidth
              label="Current Password"
              type={showCurrentPassword ? 'text' : 'password'}
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoFocus
              required
              disabled={savingPassword}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowCurrentPassword(!showCurrentPassword)} edge="end">
                        {showCurrentPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              size="small"
              fullWidth
              label="New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={savingPassword}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                        {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              size="small"
              fullWidth
              label="Confirm New Password"
              type={showNewPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={savingPassword}
              error={confirmPassword.length > 0 && newPassword !== confirmPassword}
              helperText={confirmPassword.length > 0 && newPassword !== confirmPassword ? 'Passwords do not match' : ''}
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={cancelEditPassword} disabled={savingPassword}>
                Cancel
              </Button>
              <Button
                size="small"
                type="submit"
                variant="contained"
                disabled={savingPassword || !currentPassword || !newPassword || newPassword !== confirmPassword}
              >
                {savingPassword ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography variant="body1">••••••••</Typography>
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* About Me Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="subtitle2" color="text.secondary">About Me</Typography>
          {!editingAboutMe && (
            <Button size="small" onClick={startEditAboutMe} startIcon={<Edit fontSize="small" />}>
              Edit
            </Button>
          )}
        </Box>
        {editingAboutMe ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              value={aboutMe}
              onChange={(e) => setAboutMe(e.target.value)}
              autoFocus
              disabled={savingAboutMe}
              placeholder="Tell others about yourself..."
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button size="small" onClick={cancelEditAboutMe} disabled={savingAboutMe}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={saveAboutMe} disabled={savingAboutMe}>
                {savingAboutMe ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {aboutMe || <Typography component="span" color="text.secondary" sx={{ fontStyle: 'italic' }}>Nothing here yet. Tap Edit to add something about yourself.</Typography>}
          </Typography>
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Notifications Section */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Notifications fontSize="small" color="action" />
            <Typography variant="subtitle2" color="text.secondary">Push Notifications</Typography>
          </Box>
          <Switch
            checked={notificationsEnabled}
            onChange={async () => {
              try {
                await toggleNotifications()
              } catch {
                enqueueSnackbar('Failed to update notification settings', { variant: 'error' })
              }
            }}
            disabled={notificationsLoading || permissionState === 'unsupported'}
          />
        </Box>
        {permissionState === 'unsupported' && (
          <Typography variant="caption" color="text.secondary">
            Push notifications are not supported in this browser.
          </Typography>
        )}
        {permissionState === 'denied' && (
          <Typography variant="caption" color="text.secondary">
            Notification permission was denied. You may need to enable it in your browser settings.
          </Typography>
        )}
      </Paper>

      <Divider sx={{ my: 3 }} />

      {/* Family Section */}
      <Box sx={{ mb: 3 }}>        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            <FamilyRestroom sx={{ verticalAlign: 'middle', mr: 1 }} />
            My Family
          </Typography>
        </Box>
        {loadingFamily ? (
          <Skeleton variant="rounded" height={60} />
        ) : !family ? (
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography color="text.secondary" gutterBottom sx={{ fontWeight: 700 }}>
                You haven't joined a family yet.
                </Typography>
              <Button variant="contained" color='secondary' size="small" onClick={() => setCreateFamilyOpen(true)}>
                Create Your Family
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate(`/families/${family.id}`)}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{family.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {family.memberCount} member{family.memberCount !== 1 ? 's' : ''}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      <Button
        variant="outlined"
        color="error"
        fullWidth
        onClick={handleLogout}
      >
        Logout
      </Button>

      {/* Create Family Dialog */}
      <Dialog open={createFamilyOpen} onClose={() => setCreateFamilyOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Create Family</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth label="Family Name" value={newFamilyName}
            onChange={e => setNewFamilyName(e.target.value)} margin="dense"
            onKeyDown={e => e.key === 'Enter' && handleCreateFamily()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateFamilyOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateFamily} variant="contained" disabled={creatingFamily || !newFamilyName.trim()}>
            {creatingFamily ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
